import React, { useState, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Layout, Spin, theme } from "antd";
import Header from "./components/Header";
import OfflineStatusIndicator from "./components/OfflineStatusIndicator";
import DataPreloadIndicator from "./components/DataPreloadIndicator";
import { useOfflineDetection } from "./hooks/useOfflineDetection";
import { useDataPreload } from "./hooks/useDataPreload";
import Media from 'react-media';

// Lazy load components for better performance
const SurveyForm = lazy(() => import("./components/surveyFormRefactored"));
const AdminLogin = lazy(() => import("./components/Auth/AdminLogin"));
const AdminDashboard = lazy(() => import("./components/Dashboard/AdminDashboard"));
const LandingPage = lazy(() => import("./components/LandingPage"));
const SurveyHistory = lazy(() => import("./components/SurveyHistory"));

const { Content } = Layout;

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }
  
  return user ? children : <Navigate to="/admin/login" replace />;
};

// Main Survey App (Guest Flow)
const SurveyApp = () => {
  const navigate = useNavigate();
  const [userType, setUserType] = useState(null);
  const [lastSubmitted, setLastSubmitted] = useState(null);
  const appId = "survey-app";

  const handleStart = (type) => {
    // Create proper userType object
    const userTypeObj = {
      key: type,
      title: type.charAt(0).toUpperCase() + type.slice(1).replace('-', ' ')
    };
    
    setUserType(userTypeObj);
    navigate(`/survey?userType=${type}`);
  };

  const handleSubmitSuccess = () => {
    setLastSubmitted(new Date().toISOString());
    setUserType(null);
    navigate('/?submitted=true');
  };

  return (
    <Layout style={{
      minHeight: '100vh',
      minHeight: '100dvh',
      width: '100%',
      overflow: 'visible'
    }}>
      <Header showAdminLink={true} />
      
      <Layout.Content style={{
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%',
        display: 'block',
        flex: '0 0 auto',
        overflow: 'visible'
      }}>
        <Suspense fallback={
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '50px 0'
          }}>
            <Spin size="large" />
          </div>
        }>
          <LandingPage onStart={handleStart} />
        </Suspense>
        
        {lastSubmitted && (
          <div style={{
            marginTop: 24,
            textAlign: 'center',
            padding: 12,
            backgroundColor: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: 8,
            color: '#52c41a'
          }}>
            Last submission: {new Date(lastSubmitted).toLocaleString()}
          </div>
        )}
      </Layout.Content>
    </Layout>
  );
};

// Survey Page Component
const SurveyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const userTypeParam = params.get('userType');
  const appId = "survey-app";

  // Create userType object from URL parameter
  const userType = userTypeParam ? {
    key: userTypeParam,
    title: userTypeParam.charAt(0).toUpperCase() + userTypeParam.slice(1).replace('-', ' ')
  } : null;

  if (!userType) {
    return <Navigate to="/" replace />;
  }

  const handleSubmitSuccess = () => {
    navigate('/?submitted=true');
  };

  const handleBack = () => {
    navigate('/');
  };

  const { token } = theme.useToken();

  return (
    <Layout style={{
      minHeight: '100vh',
      backgroundColor: token.colorBgLayout
    }}>
      <Header showAdminLink={true} />
      
      <Layout.Content style={{
        width: '100%',
        margin: 0,
        padding: 0
      }}>
        <Suspense fallback={
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '50px 0'
          }}>
            <Spin size="large" />
          </div>
        }>
          <SurveyForm
            userType={userType}
            userId={"guest"}
            appId={appId}
            onSubmitSuccess={handleSubmitSuccess}
            onBack={handleBack}
          />
        </Suspense>
      </Layout.Content>
    </Layout>
  );
};

// Admin Login Page
const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { token } = theme.useToken();

  if (user) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return (
    <Layout style={{
      minHeight: '100vh',
      backgroundColor: token.colorBgLayout
    }}>
      <Header showBackToHome={true} />
      
      <Layout.Content style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px'
      }}>
        <div style={{
          width: '100%',
          maxWidth: 400,
          backgroundColor: token.colorBgContainer,
          borderRadius: 8,
          border: `1px solid ${token.colorBorder}`,
          boxShadow: token.boxShadowTertiary
        }}>
          <h1 style={{
            textAlign: 'center',
            marginBottom: 24,
            color: token.colorText,
            padding: '32px 32px 0 32px',
            margin: 0
          }}>
            Admin Login
          </h1>
          <Suspense fallback={
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '50px 0'
            }}>
              <Spin size="large" />
            </div>
          }>
            <AdminLogin />
          </Suspense>
        </div>
      </Layout.Content>
    </Layout>
  );
};

// Admin Dashboard Page
const AdminDashboardPage = () => {
  const appId = "survey-app";
  const { token } = theme.useToken();

  return (
    <Layout style={{
      minHeight: '100vh',
      backgroundColor: token.colorBgLayout
    }}>
      <Header showLogout={true} />
      
      <Layout.Content style={{ padding: 24 }}>
        <Suspense fallback={
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '50px 0'
          }}>
            <Spin size="large" />
          </div>
        }>
          <AdminDashboard appId={appId} />
        </Suspense>
      </Layout.Content>
    </Layout>
  );
};

// Survey History Page
const SurveyHistoryPage = () => {
  const { token } = theme.useToken();

  return (
    <Layout style={{
      minHeight: '100vh',
      backgroundColor: token.colorBgLayout
    }}>
      <Header showBackToHome={true} />
      
      <Content style={{
        maxWidth: '1200px',
        margin: '0 auto',
        width: '100%'
      }}>
        <Suspense fallback={
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '50px 0'
          }}>
            <Spin size="large" />
          </div>
        }>
          <SurveyHistory />
        </Suspense>
      </Content>
    </Layout>
  );
};

// App Routes
const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<SurveyApp />} />
      <Route path="/survey" element={<SurveyPage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/survey/history" element={<SurveyHistoryPage />} />
      
      {/* Protected Admin Routes */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute>
            <AdminDashboardPage />
          </ProtectedRoute>
        }
      />
      
      {/* Redirect any unknown routes */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Main App Component
const App = () => {
  const { isOnline } = useOfflineDetection();
  
  // Initialize data preloading
  const {
    isPreloading,
    preloadError,
    preloadProgress,
    cacheStatus,
    refreshData
  } = useDataPreload({
    autoPreload: true,
    showProgress: true,
    retryOnError: true
  });

  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
        
        {/* Global Offline Status Indicator */}
        <Media queries={{
          mobile: "(max-width: 768px)",
          desktop: "(min-width: 769px)"
        }}>
          {matches => (
            <OfflineStatusIndicator compact={matches.mobile} />
          )}
        </Media>
        
        {/* Data Preloading Indicator */}
        <DataPreloadIndicator
          isPreloading={isPreloading}
          preloadProgress={preloadProgress}
          preloadError={preloadError}
          cacheStatus={cacheStatus}
          onRetry={refreshData}
          isOnline={isOnline}
        />
      </Router>
    </AuthProvider>
  );
};

export default App;
